import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader, LogOut, ArrowLeft } from 'lucide-react';
import { useAdminData } from './admin/useAdminData';
import { AdminLogin } from './admin/AdminLogin';
import { StatCards } from './admin/StatCards';
import { ProgramsTab } from './admin/ProgramsTab';
import { FlagsTab } from './admin/FlagsTab';
import { UsersTab } from './admin/UsersTab';
import { LeadsTab } from './admin/LeadsTab';
import { ReportingTab } from './admin/ReportingTab';

const TABS = [
  { key: 'leads', label: 'Leads & Submissions' },
  { key: 'reporting', label: 'Reporting' },
  { key: 'programs', label: 'Visa Programs' },
  { key: 'flags', label: 'Feature Flags' },
  { key: 'users', label: 'Users & Assessments' },
];

const CenteredLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Loader size={48} color="#F67425" className="animate-spin" />
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('leads');
  const admin = useAdminData();

  if (admin.checkingAuth) return <CenteredLoader />;

  if (!admin.isAuthenticated) {
    return <AdminLogin onLogin={admin.handleLogin} error={admin.loginError} loading={admin.loginLoading} />;
  }

  if (admin.loading) return <CenteredLoader />;

  return (
    <div style={{ minHeight: '100vh' }}>
      <header className="app-header">
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div className="logo-container" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
              <img src="/axpats-logo.webp" alt="Axpats" style={{ height: '44px', width: 'auto' }} />
            </div>
            <span className="badge badge-premium" style={{ fontSize: '0.6875rem' }}>ADMIN</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button onClick={() => navigate('/')} className="btn btn-ghost btn-sm" data-testid="admin-back-btn">
              <ArrowLeft size={16} />
              Back to Site
            </button>
            <button
              onClick={admin.handleLogout}
              data-testid="admin-logout-btn"
              className="btn btn-sm"
              style={{ background: 'var(--error-bg)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--error)' }}
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container" style={{ padding: '2rem 1.5rem' }}>
        <StatCards stats={admin.stats} programStats={admin.programStats} />

        <div style={{ display: 'flex', gap: '0', borderBottom: '1px solid var(--border-default)', marginBottom: '1.5rem' }}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              data-testid={`tab-${tab.key}`}
              style={{
                padding: '0.875rem 1.5rem',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === tab.key ? '2px solid var(--brand-purple)' : '2px solid transparent',
                color: activeTab === tab.key ? 'var(--brand-purple)' : 'var(--text-muted)',
                fontWeight: activeTab === tab.key ? 600 : 400,
                cursor: 'pointer',
                marginBottom: '-1px',
                fontSize: '0.9375rem',
                textTransform: 'capitalize',
                transition: 'all 0.2s ease'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {activeTab === 'leads' && <LeadsTab />}

        {activeTab === 'reporting' && <ReportingTab />}

        {activeTab === 'programs' && (
          <ProgramsTab
            programs={admin.programs}
            programStats={admin.programStats}
            expandedProgram={admin.expandedProgram}
            programDetails={admin.programDetails}
            actionLoading={admin.actionLoading}
            toggleProgram={admin.toggleProgram}
            verifyProgram={admin.verifyProgram}
            addRule={admin.addRule}
            addSource={admin.addSource}
            deleteRule={admin.deleteRule}
            seedPrograms={admin.seedPrograms}
          />
        )}

        {activeTab === 'flags' && (
          <FlagsTab featureFlags={admin.featureFlags} onToggle={admin.toggleFlag} />
        )}

        {activeTab === 'users' && (
          <UsersTab recentUsers={admin.recentUsers} recentAssessments={admin.recentAssessments} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
